import React, { useState } from "react";
import { TbShieldCheck, TbDownload } from "react-icons/tb";
import { useMfa } from "../../hooks/useMfa";
import { downloadRecoveryCodes } from "../../../utils/downloadRecoveryCodes";
import { Button, Field, Alert, SettingRow, Modal } from "../../components";
import "./Settings.css";

// Two-factor.
//
// Rebuilt rather than reused so v2 owns its screens, but the endpoint sequence is
// unchanged. The one thing worth being careful about: the account is not protected
// until verify-setup succeeds. Treating setup alone as "enrolled" would lock people
// out at their next sign-in.
const TwoFactorScreen = () => {
  const { status, loading, error, busy, enrolment, recoveryCodes, setRecoveryCodes, beginSetup, verifySetup, setMethod, disable } = useMfa();
  const [code, setCode] = useState("");
  const [disabling, setDisabling] = useState(false);
  const [password, setPassword] = useState("");

  if (loading) return <div className="v2-page"><p className="v2-hint">Loading…</p></div>;

  const enrolled = !!status?.totp_enabled;
  const method = status?.method;

  return (
    <div className="v2-page">
      <div className="v2-pagehead">
        <div>
          <h2>Two-factor</h2>
          <p>How your sign-ins are verified. An authenticator app generates codes instantly, with no wait for email.</p>
        </div>
      </div>

      <div className="v2-settings-form">
        <Alert>{error}</Alert>

        <div className="v2-mfa-state">
          <TbShieldCheck size={18} color={enrolled ? "var(--v2-success)" : "var(--v2-text-muted)"} />
          <div>
            <div className="v2-setting-name">
              {enrolled ? "Authenticator app enrolled" : "No authenticator enrolled"}
            </div>
            <div className="v2-setting-desc">
              {enrolled
                ? `Sign-ins are verified with ${method === "totp" ? "your authenticator app" : "emailed codes"}.`
                : "Sign-ins are verified with emailed codes."}
            </div>
          </div>
        </div>

        {!enrolled && !enrolment && (
          <div>
            <Button variant="primary" onClick={beginSetup} disabled={busy}>
              {busy ? "Preparing…" : "Set up authenticator app"}
            </Button>
          </div>
        )}

        {enrolment && (
          <div className="v2-settings-group">
            <div className="v2-section-title">Scan this with your authenticator</div>
            <div className="v2-mfa-enrol">
              {enrolment.qr && <img src={enrolment.qr} alt="Authenticator QR code" width={148} height={148} />}
              <div>
                <p className="v2-hint" style={{ marginTop: 0 }}>
                  Google Authenticator, Microsoft Authenticator, 1Password and Duo all work. If you
                  cannot scan, enter this key by hand:
                </p>
                <code className="v2-mfa-secret">{enrolment.secret}</code>
                <Field label="Six-digit code" hint="Codes rotate every 30 seconds.">
                  <input className="v2-input" inputMode="numeric" maxLength={6} value={code}
                         onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" />
                </Field>
                <Button variant="primary" disabled={busy || code.length !== 6}
                        onClick={async () => { const ok = await verifySetup(code); if (ok) setCode(""); }}>
                  {busy ? "Verifying…" : "Confirm and enable"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {enrolled && (
          <div className="v2-settings-group">
            <div className="v2-section-title">Preferred method</div>
            <SettingRow name="Authenticator app" desc="Codes generated on your device. Faster and more reliable than email.">
              <Button onClick={() => setMethod("totp")} disabled={busy || method === "totp"}>
                {method === "totp" ? "In use" : "Use this"}
              </Button>
            </SettingRow>
            <SettingRow name="Emailed codes" desc="A code is sent to your email address at each sign-in.">
              <Button onClick={() => setMethod("email")} disabled={busy || method === "email"}>
                {method === "email" ? "In use" : "Use this"}
              </Button>
            </SettingRow>
            <div style={{ marginTop: ".75rem" }}>
              <Button variant="danger" onClick={() => setDisabling(true)} disabled={busy}>
                Remove authenticator
              </Button>
            </div>
          </div>
        )}
      </div>

      {recoveryCodes && (
        <Modal title="Save your recovery codes" onClose={() => setRecoveryCodes(null)}
          footer={<>
            <Button onClick={() => downloadRecoveryCodes(recoveryCodes)}><TbDownload size={15} /> Download</Button>
            <Button variant="primary" onClick={() => setRecoveryCodes(null)}>I have saved them</Button>
          </>}>
          <Alert tone="warning">
            These are shown once. Each code works a single time and is the only way back in if you
            lose your device.
          </Alert>
          <div className="v2-recovery">
            {recoveryCodes.map((c) => <code key={c}>{c}</code>)}
          </div>
        </Modal>
      )}

      {disabling && (
        <Modal title="Remove authenticator?" onClose={() => { setDisabling(false); setPassword(""); }}
          footer={<>
            <Button onClick={() => { setDisabling(false); setPassword(""); }} disabled={busy}>Cancel</Button>
            <Button variant="danger" disabled={busy || !password}
                    onClick={async () => { const ok = await disable(password); if (ok) { setDisabling(false); setPassword(""); } }}>
              {busy ? "Removing…" : "Remove"}
            </Button>
          </>}>
          <p className="v2-hint" style={{ margin: 0 }}>
            Sign-ins will fall back to emailed codes. Confirm with your account password.
          </p>
          <Field label="Password">
            <input className="v2-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
          </Field>
        </Modal>
      )}
    </div>
  );
};

export default TwoFactorScreen;
